import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getNotifications,
  getUnreadNotificationsCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../shared/api/notifications.api'
import type { NotificationDto } from '../../shared/api/types'
import {
  formatNotificationDate,
  getNotificationRoute,
  NOTIFICATION_TYPE_LABELS,
} from './notificationUtils'

function BellIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  )
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadNotificationsCount,
    staleTime: 30_000,
  })

  const { data } = useQuery({
    queryKey: ['notifications', { limit: 10 }],
    queryFn: () => getNotifications({ page: 1, limit: 10 }),
    enabled: open,
  })

  const markReadMut = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const markAllMut = useMutation({
    // Колокольчик читает все типы разом (фильтра тут нет).
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const unreadCount = countData?.unreadCount ?? data?.unreadCount ?? 0

  const openNotification = (notification: NotificationDto) => {
    const route = getNotificationRoute(notification)
    if (!notification.isRead) markReadMut.mutate(notification.id)
    setOpen(false)
    navigate(route)
  }

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="notifications-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
        aria-label="Уведомления"
        title="Уведомления"
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="absolute -right-1 top-0 min-w-[18px] rounded-full bg-brand-600 px-1 text-center text-[10px] font-semibold leading-[18px] text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-[340px] max-w-[calc(100vw-24px)] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
            <div className="text-sm font-medium text-gray-900">Уведомления</div>
            <button
              type="button"
              onClick={() => markAllMut.mutate()}
              disabled={unreadCount === 0 || markAllMut.isPending}
              className="text-xs text-brand-600 hover:text-brand-700 disabled:text-gray-300"
            >
              Прочитать все
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {(data?.items ?? []).length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-gray-400">Пока пусто</div>
            ) : (
              data!.items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openNotification(n)}
                  className={`block w-full border-b border-gray-50 px-3 py-2 text-left transition-colors last:border-0 hover:bg-gray-50 ${
                    n.isRead ? 'bg-white' : 'bg-brand-50/70'
                  }`}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                      {NOTIFICATION_TYPE_LABELS[n.type]}
                    </span>
                    <span className="text-[11px] text-gray-400">
                      {formatNotificationDate(n.createdAt)}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-gray-900">{n.title}</div>
                  <div className="mt-0.5 line-clamp-2 text-xs text-gray-500">{n.message}</div>
                </button>
              ))
            )}
          </div>

          <Link
            data-testid="notifications-all-link"
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block border-t border-gray-100 px-3 py-2 text-center text-xs font-medium text-brand-600 hover:bg-gray-50"
          >
            Все уведомления
          </Link>
        </div>
      )}
    </div>
  )
}
